<script setup lang="ts">
import { computed, onMounted, ref, Ref, toRefs, watch, onBeforeUnmount } from 'vue';
import { RRule } from 'rrule';
import { useDateTime, useRequestCancel } from '@/composables';
import CalendarOverlappingMessage from './components/CalendarOverlappingMessage.vue';
import { CalendarService } from '@/services';
import { REQUEST, EMIT } from '@/enum';
import { useDebounceFn } from '@vueuse/core';

interface Props {
  eventId?: number;
  duration?: number;
  isRecurring: boolean;
  rrule: Partial<RRule>;
}

interface OverlappingEventInterface {
  start_at: string;
  end_at: string;
  date: string;
  time: string;
  event_id: number;
  paid: boolean;
  documented: boolean;
}

interface MessageInterface {
  main: string;
  sub: string;
  dateTime: { date: string; time: string }[];
}

const emit = defineEmits<{
  (e: EMIT.ERROR): void;
  (e: EMIT.OK): void;
}>();

const props = defineProps<Props>();

const { duration, eventId, isRecurring, rrule } = toRefs(props);

const overlaps: Ref<OverlappingEventInterface[]> = ref([]);

const showMore = ref(false);

const isLoading = ref(false);

const dateTime = useDateTime();

const requestCancel = useRequestCancel();

onMounted(() => queryOverlappingEvents());

const events = computed(() => (showMore.value ? overlaps.value : overlaps.value.slice(0, 4)));

const shouldShow = computed(() => events.value.length);

const shouldShowMore = computed(() => overlaps.value.length > 4);

const showLabel = computed(() => (showMore.value ? 'Show less' : `(${overlaps.value.length - 4}) more`));

const showLabelIcon = computed(() => (showMore.value ? 'expand_less' : 'expand_more'));

enum ERROR {
  BILLED_MAIN = 'Billable Events can not overlap.',
  BILLED_SUB = 'Please choose a different time or adjust the conflicting event and try again.',
  NOT_DOCUMENTED_MAIN = 'Unable to create overlapping event.',
  NOT_DOCUMENTED_SUB = 'Please choose a different time or document the conflicting event and try again.',
}

const messages = ref<MessageInterface[]>([
  { main: ERROR.BILLED_MAIN, sub: ERROR.BILLED_SUB, dateTime: [] },
  { main: ERROR.NOT_DOCUMENTED_MAIN, sub: ERROR.NOT_DOCUMENTED_SUB, dateTime: [] },
]);

const queryOverlappingEvents = async () => {
  if (!duration.value || duration.value <= 0) return;

  isLoading.value = true;
  const currentRRule = new RRule(rrule.value.options);
  currentRRule.origOptions.until = currentRRule.options.until;
  currentRRule.options.dtstart = new Date((currentRRule.options?.dtstart as Date).toUTCString());
  currentRRule.options.until = new Date((currentRRule.options?.until as Date).toUTCString());

  // count is not supported currently in the API
  currentRRule.options.count = null;
  currentRRule.options.byhour = [];
  currentRRule.options.byminute = [];
  currentRRule.options.bysecond = [];
  (currentRRule.options as RRule['options']).byweekday = (currentRRule.origOptions as RRule['options']).byweekday;
  (currentRRule.options as RRule['options']).bynweekday = null;
  const rrulestring = RRule.optionsToString((currentRRule as RRule).options);

  const payload = {
    start_at: dateTime.formatDateForApiWithoutConvertTimezone(currentRRule.options.dtstart, true),
    end_at: dateTime.formatDateForApiWithoutConvertTimezone(currentRRule.options.until, true),
    rrule: isRecurring.value ? rrulestring.split('\n')[1] : null,
    event_id: eventId.value || null,
    duration: duration.value,
  };

  const overlappingEvents = await CalendarService.getOverlappingEvents(payload);

  if (overlappingEvents) await defineOverlappingEvents(overlappingEvents.data.data);
  isLoading.value = false;
};
const debouncedQueryOverlappingEvents = useDebounceFn(async () => await queryOverlappingEvents(), 600, {
  maxWait: 2000,
});

watch(rrule, debouncedQueryOverlappingEvents, { deep: true });

const resetMessages = () => {
  messages.value[0].dateTime = [];
  messages.value[1].dateTime = [];
};

const defineOverlappingEvents = async (overlappingEvents: OverlappingEventInterface[]) => {
  overlaps.value = overlappingEvents;
  resetMessages();

  if (overlaps.value.length) {
    overlaps.value.forEach((event) => {
      if (event.documented && event.paid) {
        messages.value[0].dateTime.push({ date: event.date, time: event.time });
      } else if (!event.documented) {
        messages.value[1].dateTime.push({ date: event.date, time: event.time });
      }
    });
    emit(EMIT.ERROR);
  } else {
    emit(EMIT.OK);
  }
};

const onShowMore = () => (showMore.value = !showMore.value);

onBeforeUnmount(() => requestCancel.requestSaverAbort(REQUEST.OVERLAPPING_EVENTS));
</script>

<template>
  <div v-if="shouldShow" class="mt-2 ml-5">
    <Message v-if="!isLoading" severity="error" icon="null" :closable="false">
      <div class="p-2 pl-0 pr-0 text-base">
        <div class="grid">
          <div class="col-fixed">
            <Icon name="error" class="alert__icon" />
          </div>
          <div class="col mt-1">
            <div v-for="(message, index) in messages" :key="index">
              <CalendarOverlappingMessage
                v-if="message.dateTime.length"
                :message="message"
                :style-class="index > 0 ? 'mt-3' : undefined"
              />
            </div>
          </div>
          <div v-if="shouldShowMore" class="text-sm font-semibold cursor-pointer" @click="onShowMore">
            <span class="inline-block" v-text="showLabel" />
            <Icon :name="showLabelIcon" class="relative" style="top: 5px" />
          </div>
        </div>
      </div>
    </Message>
    <Skeleton v-else class="w-full" height="120px" />
  </div>
</template>

<style scoped lang="scss">
:deep(.p-message-wrapper) {
  .p-message-icon::before {
    display: none;
  }

  .alert__icon {
    color: $red;
  }
}
</style>
